import axios from 'axios'
import hash from 'object-hash'

import { commonChartOptions, commonDispersionChartOptions } from '../'
import {
  CompassAPIDispersionDataPoint,
  CompassAPIMultiSeriesChartData,
  dataLabelsFormatter,
  getDataSources,
  getMultipleLineChartProps,
  tooltipFormatterScatter,
} from '../utils/compass'
import { formatSeriesNames } from '../utils/string'
import type { BaseContext } from './'

export interface CompassAPIScatterChartData {
  data: {
    values: CompassAPIDispersionDataPoint[]
  }[]
}

export namespace Screening {
  export namespace Backtest {
    type BacktestChartId =
      | 'performance'
      | 'excess_performance'
      | 'performance_breakdown'
      | 'agg_hit_ratio'

    const SCREENING_CHART_PATHS: Record<BacktestChartId, string> = {
      performance: '/backtest/performance',
      excess_performance: '/backtest/excess-performance',
      performance_breakdown: '/backtest/performance-breakdown',
      agg_hit_ratio: '/backtest/agg-hit-ratio',
    }

    const CONTEXT_ID = 'equity:screening:backtest'
    const SCREENING_CHARTS_PATH = '/api/charts/screening'

    const BACKTEST_CHART_TITLES: Record<BacktestChartId, string> = {
      performance: 'Performances',
      excess_performance: 'CDI excess performances',
      performance_breakdown: 'Performance breakdown',
      agg_hit_ratio: 'ER x Hit ratio',
    }

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: BacktestChartId
      factor: string
      title?: string
    }

    type ChartData = CompassAPIMultiSeriesChartData | CompassAPIScatterChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: SCREENING_CHARTS_PATH + SCREENING_CHART_PATHS[config.chart],
        method: 'GET',
        params: { factor: config.factor },
      }).then((response) => response.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'agg_hit_ratio':
            return getScatterChartProps(
              config,
              data as CompassAPIScatterChartData
            )
          default:
            return getChartProps(config, data as CompassAPIMultiSeriesChartData)
        }
      },
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        chartTitle: BACKTEST_CHART_TITLES[config.chart],
        convertToPercentages: Array(data.data.length).fill(true),
      })(config, formatSeriesNames(data))

    const getScatterChartProps = (
      config: ChartConfig,
      data: CompassAPIScatterChartData
    ) => {
      const series = [
        {
          animation: false,
          data: data.data[0].values.map(({ x, y, label, symbol }) => ({
            x: x * 100,
            y: y * 100,
            name: label,
            marker: {
              symbol:
                symbol === 0
                  ? 'circle'
                  : symbol === 5
                  ? 'triangle'
                  : symbol === 6
                  ? 'triangle-down'
                  : undefined,
            },
          })),
          dataLabels: { enabled: true, formatter: dataLabelsFormatter },
          id: 'series-0',
          name: '',
          type: 'scatter',
        },
      ]

      const tooltip = {
        backgroundColor: 'rgba(247,247,247,0.85)',
        enabled: true,
        formatter: tooltipFormatterScatter({
          xAxisLabel: 'Hit Ratio',
          xAxisSuffix: '%',
          yAxisLabel: 'Median Excess Return',
          yAxisSuffix: '%',
        }),
        shared: true,
        style: {
          color: '#333333',
          cursor: 'default',
          fontSize: '12px',
          whiteSpace: 'nowrap',
        },
        useHTML: true,
        zIndex: 1000,
      }

      const getPercentAxis = (text: string) => ({
        title: { text },
        labels: { format: '{value:.2f}%' },
      })

      return {
        id: Context.getChartId(config),
        title: BACKTEST_CHART_TITLES[config.chart],
        options: {
          ...commonChartOptions,
          ...commonDispersionChartOptions,
          ...getDataSources(null),
          series,
          tooltip,
          xAxis: getPercentAxis('Hit ratio'),
          yAxis: getPercentAxis('Median excess return'),
        },
        stockTools: false,
        useRegularChart: true,
      }
    }
  }

  export namespace Funds {
    type FundsChartId = 'historical_data'

    const CONTEXT_ID = 'equity:screening:funds'
    const SCREENING_CHARTS_PATH = '/api/charts/screening'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: FundsChartId
      fund: string
      factors: string[]
      title?: string
    }

    type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: SCREENING_CHARTS_PATH + '/funds/historical-data',
        method: 'POST',
        data: { fund: config.fund, factors: config.factors },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        { chartTitle: config.fund },
        { type: Array(data.data.length).fill('spline') }
      )(config, formatSeriesNames(data))

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config, {
          unorderedArrays: true,
        })}`,
      getChartData,
      getChartProps,
    }
  }
}
